import { useEffect, useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { Review } from '@dnyaneshwari/shared';
import {
  SOURCES, SOURCE_LABEL, blankReview, getReviews, saveReviews, tidy,
  type Source,
} from '@/services/reviewsService';

/**
 * The reviews shown on shridnyaneshwari.com.
 *
 * These are gathered by hand -- WhatsApp, the stores, email, conversation --
 * so nothing here is verified and nothing arrives on its own. They are
 * quotations chosen for publication, which is why each one has a switch and
 * why the order is set here rather than by a date.
 *
 * Saving writes content/reviews, which the website reads on its next load.
 * There is no deploy in the loop.
 */
export default function TestimonialsPage() {
  const [reviews, setReviews] = useState<Review[] | null>(null);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    void getReviews()
      .then(setReviews)
      .catch((e) => { setReviews([]); setStatus(`Could not load: ${(e as Error).message}`); });
  }, []);

  function edit(id: string, patch: Partial<Review>) {
    setReviews((list) => (list ?? []).map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setDirty(true);
  }

  function move(i: number, by: number) {
    setReviews((list) => {
      const next = (list ?? []).slice();
      const j = i + by;
      if (j < 0 || j >= next.length) return next;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    setDirty(true);
  }

  function add() {
    setReviews((list) => [blankReview(), ...(list ?? [])]);
    setDirty(true);
  }

  function remove(id: string) {
    const r = (reviews ?? []).find((x) => x.id === id);
    if (!confirm(`Delete the review from ${r?.name || 'this reader'}? This cannot be undone.`)) return;
    setReviews((list) => (list ?? []).filter((x) => x.id !== id));
    setDirty(true);
  }

  async function save() {
    const list = reviews ?? [];
    const i = list.findIndex((r) => !r.text.trim());
    if (i >= 0) {
      return setStatus(
        `Review ${i + 1}${list[i].name ? ` (${list[i].name})` : ''} has nothing in "Their words". `
        + 'That is the box the website shows — the second box is only an optional translation. '
        + 'Fill it in, or delete the review.'
      );
    }
    setBusy(true);
    try {
      await saveReviews(list.map(tidy));
      setDirty(false);
      const live = list.filter((r) => r.published).length;
      setStatus(`Saved. ${live} of ${list.length} showing on the website.`);
    } catch (e) {
      setStatus(`Save failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  if (!reviews) return <p className="p-6 text-sm text-gray-500">Loading…</p>;

  const live = reviews.filter((r) => r.published).length;

  return (
    <div className="max-w-4xl p-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Website reviews</h1>
          <p className="mt-1 text-sm text-gray-500">
            What readers have sent you, shown on shridnyaneshwari.com. Saving publishes
            straight away — the site reads this on its next load, with no deploy.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button onClick={add} className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700">
            <PlusIcon className="h-4 w-4" /> Add
          </button>
          <button
            onClick={() => void save()}
            disabled={busy || !dirty}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
          >
            {dirty ? 'Save' : 'Saved'}
          </button>
        </div>
      </div>

      <p className="mt-3 text-xs text-gray-500">
        {reviews.length} in all · <span className="font-semibold text-green-700">{live} showing</span> ·
        they appear on the site in this order
      </p>
      {status && <p className="mt-2 text-sm text-gray-700">{status}</p>}

      <div className="mt-5 space-y-4">
        {reviews.map((r, i) => (
          <div
            key={r.id}
            className={`rounded-xl border bg-white p-4 ${r.published ? 'border-gray-200' : 'border-dashed border-gray-300 bg-gray-50'}`}
          >
            <div className="flex items-center gap-2">
              <span className="w-6 text-xs font-bold text-gray-400">{i + 1}</span>
              <input
                value={r.name}
                onChange={(e) => edit(r.id, { name: e.target.value })}
                placeholder="Name (optional)"
                className="w-44 rounded-lg border-gray-300 text-sm font-semibold"
              />
              <input
                value={r.place ?? ''}
                onChange={(e) => edit(r.id, { place: e.target.value })}
                placeholder="Town"
                className="w-32 rounded-lg border-gray-300 text-sm"
              />
              <select
                value={r.source ?? 'other'}
                onChange={(e) => edit(r.id, { source: e.target.value as Source })}
                className="rounded-lg border-gray-300 text-sm"
              >
                {SOURCES.map((s) => <option key={s} value={s}>{SOURCE_LABEL[s]}</option>)}
              </select>
              <select
                value={String(r.rating ?? 0)}
                onChange={(e) => edit(r.id, { rating: Number(e.target.value) })}
                className="rounded-lg border-gray-300 text-sm"
                title="Leave as none where they did not give a rating"
              >
                <option value="0">no stars</option>
                {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{'★'.repeat(n)}</option>)}
              </select>
              <input
                type="date"
                value={r.receivedAt ?? ''}
                onChange={(e) => edit(r.id, { receivedAt: e.target.value })}
                className="rounded-lg border-gray-300 text-xs"
              />

              <div className="ml-auto flex items-center gap-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="rounded p-1 text-gray-400 disabled:opacity-25" title="Move up">
                  <ArrowUpIcon className="h-4 w-4" />
                </button>
                <button onClick={() => move(i, 1)} disabled={i === reviews.length - 1} className="rounded p-1 text-gray-400 disabled:opacity-25" title="Move down">
                  <ArrowDownIcon className="h-4 w-4" />
                </button>
                <label className="ml-2 flex items-center gap-1 text-xs font-semibold text-gray-600">
                  <input type="checkbox" checked={r.published} onChange={(e) => edit(r.id, { published: e.target.checked })} className="rounded" />
                  show
                </label>
                <button onClick={() => remove(r.id)} className="ml-1 rounded p-1 text-red-500" title="Delete">
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Two boxes that looked alike cost Zeya a save: he typed the
                review into the translation and the first stayed empty. They
                are labelled now, and the required one is marked and outlined
                in red while it is blank. */}
            <label className="mt-3 block">
              <span className="text-xs font-semibold text-gray-700">
                Their words <span className="text-red-600">*</span>
                <span className="ml-1 font-normal text-gray-400">— this is what the website shows</span>
              </span>
              <textarea
                value={r.text}
                onChange={(e) => edit(r.id, { text: e.target.value })}
                rows={3}
                placeholder="Paste the review here, in the language they wrote it"
                className={`mt-1 w-full rounded-lg text-sm leading-relaxed ${
                  r.text.trim() ? 'border-gray-300' : 'border-red-300 bg-red-50/40'
                }`}
              />
            </label>
            <label className="mt-2 block">
              <span className="text-xs font-semibold text-gray-500">
                English rendering
                <span className="ml-1 font-normal text-gray-400">
                  — optional. Leave empty and the original shows on the English site too
                </span>
              </span>
              <textarea
                value={r.textEn ?? ''}
                onChange={(e) => edit(r.id, { textEn: e.target.value })}
                rows={2}
                className="mt-1 w-full rounded-lg border-gray-200 bg-gray-50 text-sm leading-relaxed text-gray-700"
              />
            </label>
          </div>
        ))}

        {reviews.length === 0 && (
          <p className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
            Nothing here yet. Press <b>Add</b> and paste in the first one.
          </p>
        )}
      </div>
    </div>
  );
}
